/* global window */
import axios from 'axios';
import config from '../Config/config';
import { getCalendarUsername } from '../Utility/inviteLink';
import { zonedSettingsFields } from '../Utility/zonedTime';

function getParameterValue(myParam) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(myParam);
}

class CalendarRepository {
  constructor() {
    this.getSignInUrls = this.getProviders.bind(this);
    this.getSettings = this.getSettings.bind(this);
    this.createEvent = this.createEvent.bind(this);
    this.getEvents = this.getEvents.bind(this);
    this.setSettings = this.setSettings.bind(this);
    this.revokeCalendars = this.revokeCalendars.bind(this);
    this.updateEvent = this.updateEvent.bind(this);

    const authToken = getParameterValue('authToken');
    const appName = getParameterValue('app');
    this.calendarAxios = axios.create({
      baseURL: config.urls.aryaCalendarApi,
      headers: {
        'x-access-token': authToken,
        application: appName,
      },
    });
    // Read per request: an invite-token link learns its recruiter only once the invite endpoint verified it.
    this.calendarAxios.interceptors.request.use(request => ({
      ...request,
      headers: { ...request.headers, username: getCalendarUsername() },
    }));
  }

  createUser() {
    return this.calendarAxios.post('user');
  }

  getProviders() {
    return this.calendarAxios.get('providers');
  }

  getSettings() {
    return this.calendarAxios.get('_config');
  }

  createEvent(event) {
    return this.calendarAxios.post('events', {
      Location: event.Location,
      Description: event.Description,
      Attendees: event.Attendees,
      EndTime: event.EndTime,
      StartTime: event.StartTime,
      Summary: event.Summary,
      Initiator: getCalendarUsername(),
      RefererHost: getParameterValue('refererHost'),
      SessionId: event.SessionId,
      EventType: event.EventType,
      UserIds: event.UserIds,
      ReferenceId: event.ReferenceId,
      EventId: event.EventId,
    });
  }

  getEvents(timeSlot) {
    const params = {
      start_date: timeSlot.start_date,
      end_date: timeSlot.end_date,
    };
    return this.calendarAxios.get('events', {
      params,
    });
  }

  getBusySlots(timeSlot) {
    const params = {
      start_date: timeSlot.start_date,
      end_date: timeSlot.end_date,
    };
    return this.calendarAxios.get('busyslots', {
      params,
    });
  }

  setSettings(settings) {
    const body = {
      UserId: getCalendarUsername(),
      StartingFreeTime: settings.workingHoursStart,
      EndingFreeTime: settings.workingHoursEnd,
      AvailableWeekDays: settings.availableWeekDays,
      SlotDuration: settings.SlotDuration,
      BufferDuration: settings.BufferDuration,
    };
    let zonedFields;
    if (settings.TimeZone) {
      try {
        zonedFields = zonedSettingsFields(settings);
      } catch (error) {
        return Promise.reject(error);
      }
      if (!zonedFields) return Promise.reject(new Error('Invalid time zone or working hours'));
      Object.assign(body, zonedFields);
    }
    return Promise.resolve(this.calendarAxios.put('_config', body)).then(response => ({ response, zonedFields }));
  }

  revokeCalendars(providers) {
    return this.calendarAxios.post('providers/_revoke', providers);
  }

  updateEvent(details) {
    return this.calendarAxios.put(`events/${details.EventId}`, {
      EventId: details.EventId,
      EndTime: details.EndTime,
      StartTime: details.StartTime,
      Attendee: getCalendarUsername(),
      AttendeeStatus: details.AttendeeStatus,
      EventMethod: null,
      RefererHost: getParameterValue('refererHost'),
    });
  }

  deleteEvent(eventId) {
    return this.calendarAxios.post(`events/${eventId}/_delete`);
  }

  getFreeSlots(interval, type, userIds) {
    const params = {
      start_date: interval.start_date,
      end_date: interval.end_date,
      ...(type && { type }),
      ...(userIds && userIds.length > 0 && { user_ids: userIds.join(',') }),
    };
    return this.calendarAxios.get('freeslots', { params });
  }

  getEventById(eventId) {
    return this.calendarAxios.get(`events/${eventId}`);
  }

  updateEventById(eventId, event) {
    return this.calendarAxios.put(`events/${eventId}`, event);
  }
}

export { CalendarRepository, getParameterValue };
